/**
 * Single global Redux store built with @reduxjs/toolkit.
 *
 * Replaces the per-feature composeReduxStore() factory. State persists across
 * route navigation for migrated features (banners, files, notifications,
 * promotions) and for non-migrated features via dynamic `injectReducer()`.
 *
 * redux-persist is scoped at the root with a whitelist of `sypiNewView` only,
 * preserving the previous persistence behavior for the sypi-config feature.
 */
import {
  combineReducers,
  configureStore,
  Reducer,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import type { Action, Dispatch } from "redux";
import { setAutoFreeze } from "immer";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import {
  persistReducer,
  persistStore,
  createTransform,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";

// Legacy shared helpers (sortByKeyAndOrder in @utils/array-helpers, several
// interface functions, and various selectors) mutate arrays in place via
// `.sort()`, `.reverse()`, `.push()` etc. against values read from state.
// createSlice runs reducers through Immer, which freezes returned state and
// causes those in-place mutations to throw "Cannot assign to read only
// property" — most visibly on the SYFTableTotal view. Disable auto-freeze
// globally to match the pre-migration vanilla-Redux mutability contract.
// The migrated slice reducers still use Immer's `produce` internally for
// safe writes, so this only removes the *outbound* freeze on returned state.
setAutoFreeze(false);

// Shared reducers
import configReducer from "@reducers/configReducer";
import importAssetsReducer from "@reducers/importAssetsReducer";
import segmentReducer from "@reducers/segmentReducer";

// Migrated feature slice reducers (eagerly registered)
import bannersReducer from "@features/banners/reducers/bannersReducer";
import bannerAllViewReducer from "@features/banners/reducers/bannerAllViewReducer";
import bannerNewViewReducer from "@features/banners/reducers/bannerNewViewReducer";
import bannerPositionsReducer from "@features/banners/reducers/bannerPositionsReducer";
import filesReducer from "@features/files/reducers/filesReducer";
import fileAllViewReducer from "@features/files/reducers/fileAllViewReducer";
import fileNewViewReducer from "@features/files/reducers/fileNewViewReducer";
import notificationsReducer from "@features/notifications/reducers/notificationsReducer";
import notificationAllViewReducer from "@features/notifications/reducers/notificationAllViewReducer";
import notificationNewViewReducer from "@features/notifications/reducers/notificationNewViewReducer";
import promotionsReducer from "@features/promotions/reducers/promotionsReducer";
import promotionAllViewReducer from "@features/promotions/reducers/promotionAllViewReducer";
import promotionNewViewReducer from "@features/promotions/reducers/promotionNewViewReducer";

// Reset per-session sypiNewView fields on each app load: `rehydrated` so fetchSypi
// re-runs post-rehydration, and `actionTab`/`fieldVisibility` so the expand/collapse
// default (expandAll) always wins over any persisted value from prior sessions.
const resetRehydratedTransform = createTransform(
  (inboundState) => inboundState,
  (outboundState: any) => ({
    ...outboundState,
    rehydrated: false,
    actionTab: "expandAll",
    fieldVisibility: {},
  }),
  { whitelist: ["sypiNewView"] }
);

const persistConfig = {
  key: "root",
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: ["sypiNewView"],
  transforms: [resetRehydratedTransform],
};

// Reducers registered at store creation
// Use a loose Reducer signature so non-migrated feature reducers with narrow
// action types can also be injected via injectReducer() without type conflicts.
type AnyReducer = Reducer<any, any, any>;

const staticReducers: Record<string, AnyReducer> = {
  // shared
  config: configReducer,
  importAssets: importAssetsReducer,
  segmentDetails: segmentReducer,
  // banners
  banners: bannersReducer,
  bannerAllView: bannerAllViewReducer,
  bannerNewView: bannerNewViewReducer,
  bannerPositions: bannerPositionsReducer,
  // files
  fileLocations: filesReducer,
  fileAllView: fileAllViewReducer,
  fileNewView: fileNewViewReducer,
  // notifications
  notifications: notificationsReducer,
  notificationAllView: notificationAllViewReducer,
  notificationNewView: notificationNewViewReducer,
  // promotions
  promotions: promotionsReducer,
  promotionAllView: promotionAllViewReducer,
  promotionNewView: promotionNewViewReducer,
};

// Non-migrated feature reducers injected on demand.
const asyncReducers: Record<string, AnyReducer> = {};

// Dispatched after a reducer override/restore to reset the swapped slices so
// they adopt the new reducer's initial state instead of the prior one's shape.
const REPLACE_SLICES = "@@store/REPLACE_SLICES";

const createRootReducer = (): Reducer => {
  const combined = combineReducers({ ...staticReducers, ...asyncReducers });
  const resettable = (state: any, action: any): any => {
    if (action?.type === REPLACE_SLICES && state && Array.isArray(action.keys)) {
      state = { ...state };
      for (const key of action.keys) delete state[key];
    }
    return combined(state, action);
  };
  return persistReducer(persistConfig, resettable) as Reducer;
};

export const store = configureStore({
  reducer: createRootReducer(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Legacy reducers persist Moment instances (via `timeEnrichment` in
      // shared/reducers/_utils/reducer-helpers) directly into state. RTK's
      // serializableCheck deep-scans every action's state slice on dispatch
      // and throws on non-plain values, which surfaces on nearly every
      // action (LOAD_BANNERS, HIDE_SPINNER, etc.) and crashes pages with
      // large lists such as /files. Disable until the offending reducers are
      // migrated to store ISO strings only.
      serializableCheck: false,
      // A handful of legacy reducer paths mutate state in place through
      // shared helpers. RTK's immutableCheck can also become expensive on
      // the large nested trees these features load. Disable for now; the
      // migrated slices use Immer via createSlice which enforces immutability
      // at the slice level anyway.
      immutableCheck: false,
    }),
});

export const persistor = persistStore(store);

/**
 * Dynamically register a reducer under the given state key.
 * Idempotent: registering the same key twice is a no-op.
 *
 * Used by page-template.tsx to lazy-load reducers for non-migrated features
 * without eagerly bundling their code into the initial chunk.
 */
export const injectReducer = (key: string, reducer: AnyReducer): void => {
  if (asyncReducers[key] || staticReducers[key]) {
    return;
  }
  asyncReducers[key] = reducer;
  store.replaceReducer(createRootReducer());
};

/**
 * Bulk variant of injectReducer that only rebuilds the root reducer once.
 */
export const injectReducers = (reducers: Record<string, AnyReducer>): void => {
  let changed = false;
  for (const [key, reducer] of Object.entries(reducers)) {
    if (!asyncReducers[key] && !staticReducers[key]) {
      asyncReducers[key] = reducer;
      changed = true;
    }
  }
  if (changed) {
    store.replaceReducer(createRootReducer());
  }
};

/**
 * Force-register reducers under their keys even when a static reducer already
 * owns them (asyncReducers win in createRootReducer). Swapped slices are reset
 * to the new reducer's initial state. Pair with restoreStaticReducers to revert.
 */
export const overrideReducers = (reducers: Record<string, AnyReducer>): void => {
  const keys: string[] = [];
  for (const [key, reducer] of Object.entries(reducers)) {
    if (asyncReducers[key] !== reducer) {
      asyncReducers[key] = reducer;
      keys.push(key);
    }
  }
  if (keys.length) {
    store.replaceReducer(createRootReducer());
    store.dispatch({ type: REPLACE_SLICES, keys });
  }
};

/**
 * Revert keys previously overridden via overrideReducers back to their static
 * reducer and reset their state. No-op for keys that were never overridden.
 */
export const restoreStaticReducers = (keys: string[]): void => {
  const restored: string[] = [];
  for (const key of keys) {
    if (asyncReducers[key] && staticReducers[key]) {
      delete asyncReducers[key];
      restored.push(key);
    }
  }
  if (restored.length) {
    store.replaceReducer(createRootReducer());
    store.dispatch({ type: REPLACE_SLICES, keys: restored });
  }
};

/**
 * @deprecated Non-migrated feature interfaces still call `getStore()` to reach
 * the singleton. Returns the single global store. New/migrated code should use
 * `useAppDispatch()` and `useAppSelector()` from React components, or thunk
 * closures for interface functions.
 */
export const getStore = () => store;

export type RootState = ReturnType<typeof store.getState>;

/**
 * Dispatch type for the migrated features. Widened from the default RTK v2
 * `Dispatch<UnknownAction>` to `Dispatch<Action>` (which only requires
 * `{ type: string }`) so legacy action-creator return types — declared as
 * `interface FooAction { type: "FOO"; ... }` without an index signature — can
 * still be dispatched without a cast. `ThunkDispatch` is intersected so
 * `dispatch(someThunk())` remains type-safe.
 */
export type AppDispatch = ThunkDispatch<RootState, undefined, Action> &
  Dispatch<Action>;

/**
 * Typed hooks that pre-bind RootState and AppDispatch. Use these instead of
 * plain `useDispatch` / `useSelector` in the migrated features.
 */
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Store-like handle for interface functions that need to interoperate with
 * legacy shared interfaces (e.g., `getConfigurationDetails(store)`) from inside
 * an RTK thunk closure. Construct it as `{ dispatch, getState }` inside a
 * thunk instead of reaching for the deprecated `getStore()` shim.
 */
export interface AppStoreLike {
  dispatch: AppDispatch;
  getState: () => RootState;
}
